import React, { memo, useState, useEffect, useRef } from "react"
import { Link, useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"
import logoWhite from "../images/logo/logo_white_LST_wo_tag.png"
import logoBlack from "../images/logo/logo_black_LST_wo_tag.png"
import KnowUs from "./KnowUs"
import HowWeHelp from "./HowWeHelp"
import OurPrimaryFocus from "./OurPrimaryFocus"
import InformationHub from "./InformationHub"
import "../styles/header.scss"

const NAV_ITEMS = [
  { key: "know-us",           label: "Who We Are" },
  { key: "How-We-Help",       label: "How We Help" },
  { key: "our-primary-focus", label: "Our Primary Focus" },
  { key: "information-hub",   label: "Information Hub" },
]

const Chevron = ({ open }) => (
  <svg
    width="11" height="11" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const Header = () => {
  const [activeTab, setActiveTab]   = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const wrapRef = useRef(null)

  const queryData = useStaticQuery(graphql`
    query HeaderQuery {
      VMLogoWhite: file(relativePath: { eq: "logo_white_LST.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      VMLogoBlack: file(relativePath: { eq: "logo_black_LST.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      UMTVisiomate: file(relativePath: { eq: "lifeatvisiomate/UMT_Visiomate3.jpg" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      umrahshuttleapp: file(relativePath: { eq: "mobile/umrahshuttleapp.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      perimflowForMenu: file(relativePath: { eq: "news/perimflowop2.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      learningcircle: file(relativePath: { eq: "news/learning_circle.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      instagram: file(relativePath: { eq: "instagram.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      facebook: file(relativePath: { eq: "facebook.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      linkedin: file(relativePath: { eq: "linkedin.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      twitter: file(relativePath: { eq: "twitter_bird.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
      youtube: file(relativePath: { eq: "youtube.png" }) {
        childImageSharp { fluid { ...GatsbyImageSharpFluid } }
      }
    }
  `)

  const toggleTab = key => setActiveTab(prev => prev === key ? null : key)

  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setActiveTab(null)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <header ref={wrapRef} className="ant-layout-header site-header">
      {/* ── Top bar ── */}
      <div className="site-header__bar">

        <Link to="/" className="site-header__logo">
          <img src={logoWhite} alt="Visiomate" />
        </Link>

        <nav className="site-header__nav">
          {NAV_ITEMS.map(({ key, label }) => (
            <button
              key={key}
              className={`site-header__nav-btn${activeTab === key ? " is-active" : ""}`}
              onClick={() => toggleTab(key)}
            >
              {label} <Chevron open={activeTab === key} />
            </button>
          ))}
        </nav>

        <div className="site-header__cta">
          <Link to="/career" className="site-header__btn site-header__btn--filled">
            Explore Careers
          </Link>
          <Link to="/contact" className="site-header__btn site-header__btn--outline">
            Let's Talk Business
          </Link>
        </div>

        <button
          className="site-header__hamburger"
          onClick={() => setMobileOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            {mobileOpen
              ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
              : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
            }
          </svg>
        </button>
      </div>

      {/* ── Desktop dropdown panel ── */}
      {activeTab && (
        <div className="site-header__dropdown">
          {activeTab === "know-us"           && <KnowUs />}
          {activeTab === "How-We-Help"       && <HowWeHelp />}
          {activeTab === "our-primary-focus" && <OurPrimaryFocus queryData={queryData} />}
          {activeTab === "information-hub"   && <InformationHub queryData={queryData} />}
        </div>
      )}

      {/* ── Mobile nav panel ── */}
      {mobileOpen && (
        <div className="site-header__mobile-panel">
          {NAV_ITEMS.map(({ key, label }) => (
            <div key={key} className="site-header__mobile-group">
              <button
                className={`site-header__mobile-item${activeTab === key ? " is-active" : ""}`}
                onClick={() => toggleTab(key)}
              >
                {label} <Chevron open={activeTab === key} />
              </button>
              {activeTab === key && (
                <div className="site-header__mobile-content">
                  {key === "know-us"           && <KnowUs />}
                  {key === "How-We-Help"       && <HowWeHelp />}
                  {key === "our-primary-focus" && <OurPrimaryFocus queryData={queryData} />}
                  {key === "information-hub"   && <InformationHub queryData={queryData} />}
                </div>
              )}
            </div>
          ))}
          <div className="site-header__mobile-cta">
            <Link to="/career" className="site-header__btn site-header__btn--filled">Explore Careers</Link>
            <Link to="/contact" className="site-header__btn site-header__btn--outline">Let's Talk Business</Link>
          </div>
        </div>
      )}

    </header>
  )
}

export default memo(Header)
